<template>
  <div v-if="info" class="imp-banner" role="status" aria-live="polite">
    <i class="fa-solid fa-eye imp-icon" aria-hidden="true"></i>
    <span class="imp-text">
      {{ working ? 'Working as' : 'Viewing as' }} <strong>{{ info.target_name || 'someone' }}</strong><template v-if="label && !working"> ({{ label }})</template>
      <span v-if="info.real_name" class="imp-real">· signed in as {{ info.real_name }}</span>
    </span>
    <span v-if="note" class="imp-note">{{ note }}</span>
    <button type="button" class="imp-stop" :disabled="stopping" @click="stop">
      <i class="fa-solid fa-xmark" aria-hidden="true"></i> {{ stopping ? 'Stopping…' : 'Stop' }}
    </button>
  </div>
</template>

<script>
// Persistent "Viewing as …" strip across the top of the app while impersonating, ported from swiftMarketing
// (connectGS3.0 components/shell/ImpersonationBanner.vue); the same component swiftApp has. App.vue renders it;
// html.gs-imp sets --gs-imp-h and pushes #app down by that much so the 100vh shell keeps fitting the window.
// Start and stop reload the page, so the details read once on mount are always current.
import { getActAs, stopImpersonation } from "@/helpers/impersonation.js"

export default {
  name: "ImpersonationBanner",
  data() {
    return {
      info: getActAs(),
      stopping: false
    }
  },
  computed: {
    label() {
      return this.info?.target_label
    },
    working() {
      return this.info?.via === "delegation"
    },
    // swiftMarketing's other notes ("sending and approvals off" ...) describe guard rails only its GSDesign endpoints
    // enforce; swiftLoan's own endpoints (Swift API) don't, so they would overstate what is turned off here.
    note() {
      return this.info?.read_only ? "Read-only" : ""
    }
  },
  watch: {
    info: {
      immediate: true,
      handler(v) {
        document.documentElement.classList.toggle("gs-imp", !!v)
      }
    }
  },
  mounted() {
    window.addEventListener("gs-imp-refresh", this.refresh)
  },
  beforeUnmount() {
    window.removeEventListener("gs-imp-refresh", this.refresh)
    document.documentElement.classList.remove("gs-imp")
  },
  methods: {
    refresh() {
      this.info = getActAs()
    },
    async stop() {
      this.stopping = true
      try {
        await stopImpersonation()
      } finally {
        this.stopping = false
      }
    }
  }
}
</script>

<style>
:root {
  --gs-imp-h: 0px;
}

html.gs-imp {
  --gs-imp-h: 40px;
}

html.gs-imp #app {
  box-sizing: border-box;
  padding-top: var(--gs-imp-h);
}
</style>

<style scoped>
.imp-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1100;
  height: var(--gs-imp-h);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 0 16px;
  background: repeating-linear-gradient(-45deg, #b45309, #b45309 14px, #a64b07 14px, #a64b07 28px);
  color: #fff;
  font-size: 13.5px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);
  overflow: hidden;
  white-space: nowrap;
}

.imp-icon {
  font-size: 14px;
}

.imp-text {
  overflow: hidden;
  text-overflow: ellipsis;
}

.imp-real,
.imp-note {
  opacity: 0.85;
}

.imp-note {
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.22);
  font-size: 12px;
}

.imp-stop {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border: 0;
  border-radius: 999px;
  background: #fff;
  color: #7c2d12;
  font-weight: 700;
  font-size: 12.5px;
  cursor: pointer;
}

.imp-stop:hover {
  background: #fff7ed;
}

@media (max-width: 720px) {
  .imp-real,
  .imp-note {
    display: none;
  }
}
</style>
